<template>
  <div class="exam-content">
    <div class="exam-content__modal">
      <div class="exam-content__ilu-wrapper">
        <div class="exam-content__wrapper">
          <TitleBar
            :title="title"
            type="exam"
            :currentTime="currentTime"
            @close-modal="$emit('close-modal')"
            @close-timer="showCurrentTime = false"
          />
          <div class="exam-content__wrapper-limited">
            <div
              class="exam-content__left-column"
              v-if="currentExamStep === examSteps.QUESTIONS"
            >
              <QuestionNavigation
                class="exam-content__navigation"
                :questions="questions"
                :currentPosition="currentPosition || 0"
                @move-to-question="moveToQuestion"
              />
            </div>
            <div
              class="exam-content__content-wrapper"
              ref="examcontent"
              v-if="currentExamStep === examSteps.QUESTIONS"
            >
              <div class="exam-content__content">
                <Question
                  v-if="!isExamComplete"
                  class="exam-content__question"
                  :currentQuestion="currentQuestion"
                  :totalQuestions="totalQuestions"
                  :currentPosition="currentPosition + 1"
                />
                <Answers
                  v-if="!isExamComplete"
                  class="exam-content__answers"
                  ref="answers"
                  v-bind="{
                    currentQuestion,
                    lastQuestion,
                    firstQuestion,
                    showActionButtons: true,
                    isBeta,
                    isLoading,
                    lastSavedDate,
                  }"
                  @answer-selected="commitAnswer"
                  @move-previous-question="movePreviousQuestion"
                  @open-question-feedback="openQuestionFeedback"
                  @flag-question="flagQuestion"
                />
              </div>
            </div>
            <PracticalExam
              v-if="currentExamStep === examSteps.PRACTICE"
              :attempt="attempt"
              :context="context"
              :certification="certification"
              :userLabAttempt="attempt.user_lab_attempt"
              @update-practice-state="updatePracticeState"
            />
            <ReviewExam
              v-if="currentExamStep === examSteps.REVIEW"
              :answeredQuestions="answeredQuestions"
              :totalQuestions="totalQuestions"
              :userLabAttempt="attempt.user_lab_attempt"
              :steps="examSteps"
              @go-to-step="goToStep"
            />
            <ExamFooter
              :currentStep="currentExamStep"
              :steps="examSteps"
              :lastQuestionPosition="currentPosition + 1 === totalQuestions"
              :practiceStatus="practiceStatus"
              :canGoToPractice="!isLoading"
              :canGoToReview="canGoToReview"
              @go-to-step="goToStep"
              @submit="commitAnswerAndSubmit"
              @save-answers-and-practice="commitAnswerAndPractice"
              @save-answers-and-review="commitAnswerAndReview"
            />

            <div class="exam-content__feedback" v-if="openFeedbackModal">
              <Feedback
                :title="feedbackTitle"
                :feedbackHolder="feedbackHolder"
                @give-feedback="saveFeedback"
                @close-modal="closeFeedback"
              >
                While our exams are in beta, we gather feedback from a selected
                group of students.<br />
                Your feedback will help us improve before launching.
              </Feedback>
            </div>
            <div class="exam-content__alert" v-if="openAlertModal">
              <AlertModal @close-modal="openAlertModal = false">
                <div
                  class="exam-content__alert__warning"
                  v-if="unAnsweredQuestions"
                >
                  <WaringOrangeIcon class="exam-content__alert__warning-icon" />
                  You have {{ unAnsweredQuestions }} unanswered questions.
                </div>
                <h1 class="exam-content__alert__title">
                  Are you sure you're ready to submit this exam?
                </h1>
                <p class="exam-content__alert__subtitle">
                  This action can't be undone
                </p>
                <button
                  @click="submitAttempt"
                  :disabled="isLoading"
                  class="btn btn--primary exam-content__alert__cta"
                >
                  Submit exam
                </button>
                <div
                  class="btn btn--tertiary exam-content__alert--close"
                  :disabled="isLoading"
                  @click="openAlertModal = false"
                >
                  Go Back
                </div>
              </AlertModal>
            </div>
            <div class="exam-content__finished" v-if="openFinishedModal">
              <AlertModal @close-modal="$emit('close-modal')">
                <SuccessIcon class="exam-content__alert__success" />
                <h1 class="exam-content__alert__title">
                  Thank you for your submission
                </h1>
                <p class="exam-content__alert__subtitle">
                  We're evaluating your answers. You'll be notified when the
                  results are in.
                </p>
                <button
                  @click="generalFeedback"
                  class="btn btn--primary exam-content__alert__cta"
                >
                  Next
                </button>
              </AlertModal>
            </div>
            <div class="exam-content__finished" v-if="openThankYouModal">
              <AlertModal @close-modal="$emit('close-modal')">
                <SuccessIcon class="exam-content__alert__success" />
                <h1 class="exam-content__alert__title">
                  Thank you for your feedback
                </h1>
                <p class="exam-content__alert__subtitle">
                  Your comments have been noted.
                </p>
                <button
                  @click="$emit('close-modal')"
                  class="btn btn--primary exam-content__alert__cta"
                >
                  Close
                </button>
              </AlertModal>
            </div>
            <div class="exam-content__finished" v-if="runOutOfTime">
              <AlertModal @close-modal="$emit('close-modal')">
                <div class="exam-content__alert__error">
                  <XCircleIcon class="exam-content__alert__error-icon" />
                  The time clock has expired.
                </div>
                <h1 class="exam-content__alert__title">
                  You failed to submit your exam in the allotted amount of time.
                </h1>
                <button
                  @click="$emit('close-modal')"
                  class="btn btn--primary exam-content__alert__cta"
                >
                  Close
                </button>
              </AlertModal>
            </div>
            <ExamScoreModal
              v-if="showScoreModal"
              :attempt="attempt"
              @close-score-modal="$emit('close-modal')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { highlightAllUnder } from 'prismjs'
import { intervalToDuration, differenceInMilliseconds } from 'date-fns'
import { XCircleIcon } from 'vue-feather-icons'
import CONSTANTS from '@/constants'
import SuccessIcon from '@/assets/images/certification/submission-success-icon.svg'
import WaringOrangeIcon from '@/assets/images/certification/warning-orange-icon.svg'
import Feedback from '@/components/certifications/Feedback.vue'
import AlertModal from '@/components/certifications/AlertModal.vue'
import QuestionNavigation from '@/components/certifications/QuestionNavigation.vue'
import ExamScoreModal from '@/components/modals/ExamScoreModal.vue'
import PracticalExam from '@/components/certifications/PracticalExam.vue'
import ReviewExam from '@/components/certifications/ReviewExam.vue'
import ExamFooter from '@/components/certifications/ExamFooter.vue'
import TitleBar from './quiz-modal/TitleBar.vue'
import Question from './quiz-modal/Question.vue'
import Answers from './quiz-modal/Answers.vue'
import 'prismjs/components/prism-python'

export default {
  name: 'ExamModal',
  components: {
    TitleBar,
    Question,
    Answers,
    Feedback,
    AlertModal,
    SuccessIcon: { ...SuccessIcon, name: 'SuccessIcon' },
    WaringOrangeIcon: { ...WaringOrangeIcon, name: 'WaringOrangeIcon' },
    QuestionNavigation,
    ExamScoreModal,
    PracticalExam,
    XCircleIcon,
    ExamFooter,
    ReviewExam,
  },
  data() {
    return {
      currentPosition: 0,
      selectedAnswers: [],
      isExamComplete: false,
      isExamPassed: false,
      finalScore: {},
      isLoading: false,
      openFeedbackModal: false,
      feedbackTitle: '',
      currentTime: null,
      runningTimeIntervalId: null,
      runningOfOutTimeIntervalId: null,
      openAlertModal: false,
      openFinishedModal: false,
      openThankYouModal: false,
      questionsWithAnswers: null,
      runOutOfTime: false,
      feedbackHolder: '',
      currentDuration: null,
      showCurrentTime: false,
      lastSavedDate: null,
      showScoreModal: false,
      currentExamStep: 'Questions',
      practiceStatus: CONSTANTS.CERTIFICATIONS.PRACTICE_STATUS.IDDLE,
    }
  },
  props: {
    certification: {
      type: Object,
      required: true,
    },
    voucherId: {
      type: String,
      required: true,
    },
    attempt: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: false,
      default: () => 'isolated',
    },
    context: {
      type: String,
      required: true,
    },
    parent: {
      type: Object,
      required: false,
    },
  },
  computed: {
    questions() {
      const { questions } = this.attempt
      return questions
    },
    totalQuestions() {
      return this.questions.length
    },
    currentQuestion() {
      return this.questions[this.currentPosition]
    },
    lastQuestion() {
      return this.questions[this.questions.length - 1]
    },
    firstQuestion() {
      return this.questions[0]
    },
    percentComplete() {
      return Math.floor(
        ((this.currentPosition + 1) / this.totalQuestions) * 100
      ).toString()
    },
    unAnsweredQuestions() {
      const answered = this.questions.filter(question => {
        return question.answered
      })
      return this.questions.length - answered.length
    },
    answeredQuestions() {
      const answered = this.questions.filter(question => {
        return question.answered
      })
      return answered.length
    },
    isBeta() {
      return this.attempt?.certification?.is_beta ?? true
    },
    eventContextData() {
      return {
        certification_id: this.certification?.id,
        certification_name: this.certification?.name,
        attempt_id: this.attempt?.id,
        created: this.attempt?.created,
        expiration_date: this.attempt?.expiration_date,
        learning_area_id: this.certification?.learning_area?.id,
        learning_area_name: this.certification?.learning_area?.name,
        latest_version: this.certification?.latest_version,
        is_beta: this.certification?.is_beta,
        certification_status: this.certification?.status,
        attempt_status: this.attempt?.status,
        finished_at: this.attempt?.finished_at,
        has_passed: this.attempt?.has_passed,
        score: this.attempt?.score,
      }
    },
    thereIsPracticalPart() {
      return !!this.attempt?.practice_lab
    },
    examSteps() {
      return this.thereIsPracticalPart
        ? {
            QUESTIONS: 'Questions',
            PRACTICE: 'Hands-on Lab',
            REVIEW: 'Review',
          }
        : {
            QUESTIONS: 'Questions',
            REVIEW: 'Review',
          }
    },
    canGoToReview() {
      if (this.thereIsPracticalPart) {
        return !!this.attempt.user_lab_attempt && !this.isLoading
      }
      return !this.isLoading
    },
  },
  methods: {
    setSelectedAnswers(answer_ids) {
      const { uuid: question_id } = this.currentQuestion
      this.selectedAnswers = [
        ...this.selectedAnswers,
        { question_id, answers: answer_ids },
      ]
    },
    commitAnswer(answer_ids) {
      this.setSelectedAnswers(answer_ids)
      this.setNextQuestion()
    },
    commitAnswerAndSubmit() {
      this.openAlertModal = true
    },
    async commitAnswerAndPractice() {
      this.setSelectedAnswers(this.$refs.answers.currentSelection)
      await this.submitPartialAttempt()
      this.goToStep(this.examSteps.PRACTICE)
    },
    async commitAnswerAndReview() {
      this.setSelectedAnswers(this.$refs.answers.currentSelection)
      await this.submitPartialAttempt()
      this.goToStep(this.examSteps.REVIEW)
    },
    movePreviousQuestion(answer_ids) {
      this.setSelectedAnswers(answer_ids)
      this.setPreviousQuestion()
    },
    moveToQuestion(position) {
      this.setSelectedAnswers(this.$refs.answers.currentSelection)
      this.submitPartialAttempt()
      this.currentPosition = position
    },
    async setNextQuestion() {
      if (this.currentPosition === this.totalQuestions - 1) {
        await this.submitPartialAttempt()
        this.openAlertModal = true
      } else {
        this.currentPosition = this.currentPosition + 1
        await this.submitPartialAttempt()
      }
      this.updatePrism()
    },
    async setPreviousQuestion() {
      if (this.currentPosition !== 0) {
        this.currentPosition = this.currentPosition - 1
        await this.submitPartialAttempt()
      }
      this.updatePrism()
    },
    async submitPartialAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        voucherId: this.voucherId,
        attemptId: this.attempt.id,
        submission,
        status: CONSTANTS.CERTIFICATIONS.ATTEMPT_STATUS.IN_PROGRESS,
      }
      try {
        await this.$store.dispatch('certifications/updateExamAttempt', payload)
        this.lastSavedDate = new Date()
      } catch (error) {
        this.$emit('exam-error')
      }
      this.isLoading = false
    },
    async submitAttempt() {
      this.isLoading = true
      const submission = this.generateSubmission()
      const payload = {
        voucherId: this.voucherId,
        attemptId: this.attempt.id,
        submission,
      }
      try {
        await this.$store.dispatch('certifications/finishExamAttempt', payload)
        this.sendMixpanelEvent(
          this.eventsConstants.CERTIFICATION_ATTEMPT_FINISHED,
          {
            ...this.eventContextData,
          }
        )
      } catch (error) {
        this.$emit('exam-error')
      }
      this.scoreQuiz()
      this.isLoading = false
      this.openAlertModal = false
      if (this.isBeta) {
        this.openFinishedModal = true
      } else {
        this.showScoreModal = true
        this.sendMixpanelEvent(
          this.eventsConstants.CERTIFICATION_ATTEMPT_SCORED,
          {
            ...this.eventContextData,
          }
        )
      }
    },
    generateSubmission(feedback = '', questionId = '') {
      const { questions, selectedAnswers } = this
      const submission = questions
      submission.map(question => {
        if (question.uuid === questionId) {
          question.feedback = feedback
        }
        selectedAnswers.map(userAnswer => {
          if (userAnswer.question_id === question.uuid) {
            const { answers } = question
            answers.map(answer => {
              answer.is_selected = userAnswer.answers.includes(answer.uuid)
            })
          }
        })
      })
      return submission
    },
    scoreQuiz() {
      this.isExamComplete = true
    },
    updatePrism() {
      this.$nextTick().then(() => {
        if (this.$refs.examcontent) {
          highlightAllUnder(this.$refs.examcontent)
          if (window.MathJax) {
            window.MathJax.Hub.Config({
              tex2jax: {
                inlineMath: [['$', '$']],
                displayMath: [['$$', '$$']],
                processEscapes: true,
                processEnvironments: true,
              },
              // Center justify equations in code and markdown cells. Elsewhere
              // we use CSS to left justify single line equations in code cells.
              displayAlign: 'center',
              'HTML-CSS': {
                styles: { '.MathJax_Display': { margin: 0 } },
                linebreaks: { automatic: true },
              },
            })
            window.MathJax.Hub.Queue([
              'Typeset',
              window.MathJax.Hub,
              this.$refs.examcontent,
            ])
          }
        }
      })
    },
    openQuestionFeedback() {
      this.feedbackTitle = `Feedback on Question ${this.currentPosition + 1}`
      this.questionFeedback = true
      this.openFeedbackModal = true
      this.feedbackHolder =
        'Did you understand the question?\nDid the question align with the course objectives?\nDid the question align with the course content?\nWas the question fair?\nDid the question stump you?'
    },
    generalFeedback() {
      this.feedbackTitle = `Do you have feedback about this exam?`
      this.questionFeedback = false
      this.openFinishedModal = false
      this.openFeedbackModal = true
      this.feedbackHolder =
        'Did you experience any issues navigating the exam platform?\nWere the instructions clearly outlined and easy to follow?\nWere the platform and features organized in a way that was easy to understand and use?\nHow sufficient were the features and tools at meeting your learning needs?\nWhat could have been better?'
    },
    flagQuestion() {
      this.currentQuestion.is_flagged = !this.currentQuestion.is_flagged
      this.submitPartialAttempt()
    },
    async saveFeedback(feedback) {
      let payload = {}
      if (this.questionFeedback) {
        const submission = this.generateSubmission(
          feedback,
          this.currentQuestion.uuid
        )
        payload = {
          voucherId: this.voucherId,
          attemptId: this.attempt.id,
          submission,
        }
      } else {
        payload = {
          voucherId: this.voucherId,
          attemptId: this.attempt.id,
          feedback,
        }
      }
      await this.$store.dispatch('certifications/updateExamAttempt', payload)
      this.lastSavedDate = new Date()
    },
    closeFeedback() {
      if (this.questionFeedback) {
        this.openFeedbackModal = false
      } else {
        this.openFeedbackModal = false
        this.openThankYouModal = true
      }
    },
    updateCurrentTime() {
      const duration = intervalToDuration({
        start: new Date(),
        end: new Date(this.attempt.expiration_date),
      })
      if (duration.hours) {
        this.currentTime = `${duration.hours}h${duration.minutes}m${duration.seconds}s`
      } else {
        this.currentTime = `${duration.minutes}m${duration.seconds}s`
      }
      this.currentDuration = duration
    },
    runningOutOfTime() {
      const result = differenceInMilliseconds(
        new Date(this.attempt.expiration_date),
        new Date()
      )
      this.runOutOfTime = Boolean(result <= 0)
    },
    updatePracticeState(state) {
      this.practiceStatus = state
    },
    goToStep(step) {
      this.currentExamStep = step
    },
  },
  created() {
    this.questionsWithAnswers = this.questions
    const body = document.querySelector('body')
    body.classList.add('modal-open')
    this.runningTimeIntervalId = setInterval(this.updateCurrentTime, 1000)
    this.runningOfOutTimeIntervalId = setInterval(this.runningOutOfTime, 1000)
  },
  beforeDestroy() {
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
    this.runningOfOutTimeIntervalId = clearInterval(
      this.runningOfOutTimeIntervalId
    )
  },
  destroyed() {
    this.$store.dispatch('certifications/clearExamStore', '')
    const body = document.querySelector('body')
    body.classList.remove('modal-open')
  },
  watch: {
    currentQuestion: {
      immediate: true,
      deep: true,
      handler: 'updatePrism',
    },
  },
}
</script>

<style lang="scss">
.exam-content {
  color: var(--white);
  &__modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('../../assets/images/backgrounds/dashboard_bg.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-color: var(--dark-gray);
    z-index: 205;
    overflow-y: scroll;
  }
  &__ilu-wrapper {
    position: relative;
    overflow: hidden;
    height: 100%;
  }
  &__wrapper {
    width: 100%;
    height: 100%;
    z-index: 206;
    overflow: auto;
    position: relative;
    &-limited {
      max-width: 1400px;
      display: flex;
      flex-flow: row wrap;
      justify-content: space-around;
      align-content: flex-start;
      align-items: flex-start;
      margin: 0 auto;
    }
  }
  &__content-wrapper {
    flex: 1;
    margin: 30px;
    position: relative;
    margin-bottom: 200px;
    @include tablet {
      margin: 20px 0 200px 0;
    }
  }
  &__content {
    position: relative;
    display: flex;
    margin: 0 auto;
    flex-wrap: wrap;
    padding: 40px 40px 150px 40px;
    background: white;
    background: linear-gradient(
        104.99deg,
        rgba(62, 63, 73, 0.7) -13.91%,
        rgba(62, 63, 73, 0) 100.48%
      )
      var(--dark-brighter);
    border-radius: 20px;
    position: relative;
    overflow: hidden;
    align-self: flex-start;
    @include desktop {
      flex-wrap: nowrap;
      padding: 40px 40px 110px 40px;
    }
  }
  &__left-column {
    width: 100%;
    @include mobile {
      width: auto;
    }
  }
  &__navigation {
    @include tablet {
      width: 280px;
    }
    margin: 20px 20px;
  }
  &__alert {
    &__title {
      width: 500px;
      font-size: rem(40px);
      margin: 20px auto;
      line-height: 52px;
    }
    &__subtitle {
      text-align: center;
      font-size: rem(18px);
      line-height: 24px;
      color: var(--light-blue-gray);
      max-width: 800px;
      margin-bottom: 20px;
    }
    &--close {
      width: fit-content;
      margin: 20px auto;
    }
    &__cta {
      margin: 0 auto;
      display: block;
    }
    &__success {
      fill: #02a378;
    }
    &__warning {
      color: var(--white);
      background: linear-gradient(270deg, #ca6c06 0%, #804505 100%);
      border-radius: 10px;
      width: 60%;
      margin: 10px auto;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      text-align: center;
      &-icon {
        stroke: #f8b369;
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2px;
        overflow: visible;
        margin-right: 10px;
      }
    }
    &__error {
      color: var(--white);
      background: linear-gradient(270deg, #cf2121 0%, #860606 100%);
      border-radius: 10px;
      width: 60%;
      margin: 10px auto;
      padding: 10px 20px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      text-align: center;
      justify-content: center;
      &-icon {
        stroke: var(--red);
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 2px;
        overflow: visible;
        margin-right: 10px;
      }
    }
  }
  &__submit {
    display: flex;
    margin: 30px 0;
    justify-content: flex-end;
  }
  &__question {
    width: 100%;
    @include desktop {
      width: 40%;
    }
  }
  &__answers {
    width: 100%;
    @include desktop {
      width: 60%;
    }
  }
  &__completed {
    width: 100%;
  }
  &__loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  &__loader {
    width: 5rem;
  }
  &__exit__btn {
    margin-right: 20px;
  }
}
</style>
